"use client";

import { useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { thetaApi } from "@/lib/api/theta";
import { userMessagingKey } from "@/lib/hooks/useUserMessaging";
import type { Me, Page } from "@theta/api-client";
import {
  getConversations,
  startConversation,
  type Conversation,
} from "../api/messages";

/** The inbox and the requests — ONE collection in v1, filtered by `?state=`. */
export const conversationsKey = ["v1", "conversations"] as const;

/** `GET /me`: the signed-in user AND both unread counters, in one document. */
export const meKey = ["v1", "me"] as const;

const EMPTY_PAGE: Page<Conversation> = {
  items: [],
  nextCursor: null,
  prevCursor: null,
  hasMore: false,
};

const FALLBACK_INBOX_POLL_MS = 5_000;
const FALLBACK_ME_POLL_MS = 10_000;

/**
 * The inbox list, cursor-paged and conditionally read.
 *
 * `enabled` lets the header dropdown share this exact cache without paying for
 * it on every page: the popup passes `enabled: open`, so the list is fetched
 * the first time it is opened and then kept warm by whoever else is mounted.
 *
 * The `?ifChanged=` cache is keyed by cursor because a token is only valid for
 * the document it came from; the poll is bounded to the FIRST page, exactly as
 * the old hook bounded it, because somebody who has scrolled into history is
 * reading rather than watching for new messages.
 */
export function useConversations({ enabled = true }: { enabled?: boolean } = {}) {
  const seen = useRef(new Map<string, { state: string; page: Page<Conversation> }>());
  const pollAfterMs = useRef<number | null>(null);

  return useInfiniteQuery({
    queryKey: conversationsKey,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const key = pageParam ?? "";
      const previous = seen.current.get(key);
      const result = await getConversations({
        cursor: pageParam,
        ...(previous ? { ifChanged: previous.state } : {}),
      });
      pollAfterMs.current = result.pollAfterMs;
      if (result.unchanged) return previous?.page ?? EMPTY_PAGE;
      seen.current.set(key, { state: result.state ?? "", page: result.data });
      return result.data;
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    enabled,

    refetchInterval: (query) =>
      enabled &&
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden" &&
      (query.state.data?.pages.length ?? 0) <= 1
        ? (pollAfterMs.current ?? FALLBACK_INBOX_POLL_MS)
        : (false as const),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * `GET /me` — the signed-in user, or `null` when signed out, NEVER an error
 * (§1.6). That is why the logged-in guard the old hook needed is gone: there
 * is no `ensure_logged_in` endpoint to poll into a 403 loop from /login.
 *
 * §1.10 lists `/me` as one document precisely so the header does not need
 * three polls, so this is the ONLY place the badge counters are read. Anything
 * else that wants them — the bell, the OS badge — reads this same key.
 */
export function useMe() {
  const seen = useRef<{ state: string; me: Me | null } | null>(null);
  const pollAfterMs = useRef<number | null>(null);

  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      const previous = seen.current;
      const result = await thetaApi.me(
        previous ? { ifChanged: previous.state } : {},
      );
      pollAfterMs.current = result.pollAfterMs;
      if (result.unchanged) return previous?.me ?? null;
      seen.current = { state: result.state ?? "", me: result.data };
      return result.data;
    },
    refetchInterval: () =>
      typeof document !== "undefined" && document.visibilityState !== "hidden"
        ? (pollAfterMs.current ?? FALLBACK_ME_POLL_MS)
        : (false as const),
    refetchIntervalInBackground: false,
    /**
     * ONE POLL, NOT ONE PER MOUNT. `staleTime: 0` meant every component that
     * mounted later and called `useMe()` — and on a feed page a good many do,
     * after the cards render — found the cached answer stale and refetched it.
     * Measured against production: `/me` at 950 ms and again at 1816 ms, 866 ms
     * apart, on a document whose poll interval is ten seconds.
     *
     * The interval above is what keeps this fresh, so anything inside one tick
     * of it is a request the poll was already going to make. Matching the two
     * leaves the cadence exactly where §1.10 and `pollAfterMs` put it.
     *
     * `invalidateQueries(meKey)` — what the mutations below use — ignores
     * `staleTime` and refetches immediately, so a change the viewer MADE still
     * shows up at once.
     */
    staleTime: FALLBACK_ME_POLL_MS,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * The header's message badge: conversations with something unread, muted ones
 * excluded — a number the SERVER computes, from the same document the bell
 * count comes from. `/theta/conversations/unread-count` is deleted.
 *
 * NOTE: this hook deliberately does NOT call navigator.setAppBadge — the app
 * has exactly one setAppBadge caller, in useNotifications.ts, which combines
 * this count with the notification count. When notifications move to v1 it
 * should read BOTH from `useMe()` and add them with `unreadBadgeTotal()`
 * (lib/api/theta.ts), which is one poll for the whole header (§1.10).
 */
export function useUnreadMessageCount(): number {
  const { data } = useMe();
  return data?.badges.unreadConversationCount ?? 0;
}

/**
 * "Something happened in one of your conversations that was not a message" —
 * OSO-549 (a rename) and OSO-550 (a reaction on a message you sent).
 *
 * A FLAG, NOT A COUNT, and that is the point: neither event writes a message,
 * so neither may be added to the number above without making it lie about what
 * it counts. The header draws the number when there is one and a bare dot when
 * there is not.
 *
 * It rides on the SAME `/me` document as the two counters — §1.10's whole
 * reason for putting the badges in one resource is that the header polls once.
 * Deliberately NOT a notification; the argument is on the server, at
 * `signalConversationActivity`.
 */
export function useHasUnseenDmActivity(): boolean {
  const { data } = useMe();
  return data?.badges.hasUnseenDmActivity ?? false;
}

export function useStartConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKey });
      queryClient.invalidateQueries({ queryKey: meKey });
      // "Does a thread with this person exist?" now has a different answer for
      // everyone in it — and `/beskeder/ny` guards against a second thread on
      // exactly that answer, so leaving the pre-send `null` cached for its
      // minute would let the guard wave through the split it exists to stop.
      queryClient.invalidateQueries({ queryKey: userMessagingKey });
    },
  });
}
